import axios from "axios";
import { API_URL, getAxiosConfig } from "./common";
import { Flowmodoro, TYPES } from "../types/types";

interface PaginatedRecordsResponse {
  records: Flowmodoro[];
  lastEvaluatedKey?: string;
}

// Fetch a single page of records
export const getFlowmodoroRecordsPage = async (
  limit?: number,
  lastEvaluatedKey?: string
): Promise<PaginatedRecordsResponse> => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  if (lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);
  
  const queryString = params.toString();
  const url = `${API_URL}/records${queryString ? `?${queryString}` : ''}`;
  
  const response = await axios.get(url, await getAxiosConfig());
  
  // Handle both old format (array) and new format ({ records, lastEvaluatedKey })
  if (Array.isArray(response.data)) {
    // Old backend format - return as single page with no next key
    return { records: response.data, lastEvaluatedKey: undefined };
  }
  
  return response.data;
};

// Fetch all records (handles pagination automatically)
export const getFlowmodoroRecords = async (): Promise<{ data: Flowmodoro[] }> => {
  // Get first page immediately
  const firstPage = await getFlowmodoroRecordsPage(200);
  
  // If there's no more data, return immediately
  if (!firstPage.lastEvaluatedKey) {
    return { data: firstPage.records || [] };
  }

  // Continue fetching remaining pages
  const allRecords: Flowmodoro[] = [...(firstPage.records || [])];
  let lastKey: string | undefined = firstPage.lastEvaluatedKey;

  while (lastKey) {
    const response: PaginatedRecordsResponse = await getFlowmodoroRecordsPage(100, lastKey);
    if (response.records) {
      allRecords.push(...response.records);
    }
    lastKey = response.lastEvaluatedKey;
  }

  return { data: allRecords };
};

export const addFlowmodoroRecord = async (flowmodoroRecord: Flowmodoro) => {
  const result = await axios.post(
    `${API_URL}/record`,
    { ...flowmodoroRecord, type: TYPES.RECORD },
    await getAxiosConfig()
  );
  return result;
};

export const editFlowmodoroRecord = async (flowmodoroRecord: Flowmodoro) => {
  const result = await axios.put(
    `${API_URL}/record`,
    { ...flowmodoroRecord, type: TYPES.RECORD },
    await getAxiosConfig()
  );
  return result;
};